import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { TeamMembersPanel } from "@/components/admin/TeamMembersPanel";
import { InvitationCredentialsDialog } from "@/components/admin/InvitationCredentialsDialog";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import type { InvitationCredentials } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

export function TeamAccessSettingsPage() {
  const activeTeam = useAuthStore((state) => state.activeTeam);
  const [invitationCredentials, setInvitationCredentials] = useState<InvitationCredentials | null>(null);
  const canManageTeam = Boolean(
    activeTeam && ["team_admin", "super_admin"].includes(activeTeam.role)
  );

  if (!activeTeam || !canManageTeam) {
    return <Navigate to="/settings/general" replace />;
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-studio-accent">Active team</p>
        <h2 className="mt-2 text-xl font-semibold tracking-tight text-studio-ink">Users and access</h2>
        <p className="mt-1.5 text-sm leading-6 text-studio-muted">
          Invite people to {activeTeam.name}, assign their role and remove access when work is complete.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-studio-accent-soft text-studio-accent">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <CardTitle>{activeTeam.name}</CardTitle>
              <p className="mt-1 text-xs leading-5 text-studio-muted">
                Changes on this page apply only to the active team.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <TeamMembersPanel teamId={activeTeam.id} canManage onCredentials={setInvitationCredentials} />
        </CardBody>
      </Card>

      <InvitationCredentialsDialog
        credentials={invitationCredentials}
        onClose={() => setInvitationCredentials(null)}
      />
    </div>
  );
}
